import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./UploadOptionDialog/Modal.css";
import Compressor from "compressorjs";

import ImageUploader from "../../../components/ImageUploader";
import CameraCapture from "../../../components/CameraCapture";
import ImageCropper from "../../../components/ImageCropper";
import ProceedButton from "../../../components/ProceedButton";
import { Button } from "primereact/button";
import BottomMessagePopUp from "../../../components/BottomMessagePopUp";
import DocumentUploader from '../../../components/DocumentUploader';
import Loader from "../../../components/Loader";

import useGetToken from "../../../store/selectors/get-token";
import { updateHealthEntry } from "../../../utils/backend";
import useGetOrderDetailsId from "../../../store/selectors/get-order-details";
import { useTranslation } from 'react-i18next';

const IDVerification = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isCapturingFront, setIsCapturingFront] = useState(true);
  const getOrderDetails = useGetOrderDetailsId();
  const token = useGetToken();
  const webcamRef = useRef(null);
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const [isErrorVisible, setIsErrorVisible] = useState(false);
  const [hasCameraPermission, setHasCameraPermission] = useState(false);

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const frontImageInputRef = useRef(null);
  const backImageInputRef = useRef(null);

  const nricInstructions = [
    t("healthInsurance.idVerification.nricInstructions.1"),
    t("healthInsurance.idVerification.nricInstructions.2"),
    t("healthInsurance.idVerification.nricInstructions.3")
  ];

  const showErrorMessage = (msg) => {
    setErrorMessage(msg);
    setIsErrorVisible(true);
  };

  const updateCache = (key, value) => {
    localStorage.setItem(key, value);
  };

  useEffect(() => {
    // Load cached images from localStorage
    const cachedFrontImage = localStorage.getItem("frontImageID");
    const cachedBackImage = localStorage.getItem("backImageID");

    if (cachedFrontImage) setFrontImage(cachedFrontImage);
    if (cachedBackImage) setBackImage(cachedBackImage);
  }, []);

  const capturePhoto = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        await processAndCompressImage(imageSrc);
      }
    } catch (error) {
      showErrorMessage(`${t("messages.failedToCapturePhoto")}: `+error.message);
    } finally {
      setLoading(false);
    }
  };

  const processAndCompressImage = async (imageData) => {
    const base64ToBlob = (base64) => {
      const byteString = atob(base64.split(",")[1]);
      const mimeString = base64.split(",")[0].split(":")[1].split(";")[0];
      const arrayBuffer = new Uint8Array(byteString.length);

      for (let i = 0; i < byteString.length; i++) {
        arrayBuffer[i] = byteString.charCodeAt(i);
      }

      return new Blob([arrayBuffer], { type: mimeString });
    };

    const blobImage = base64ToBlob(imageData);

    await new Promise((resolve, reject) => {
      new Compressor(blobImage, {
        quality: 0.6,
        success(compressedBlob) {
          const reader = new FileReader();
          reader.readAsDataURL(compressedBlob);
          reader.onloadend = () => {
            if (isCapturingFront) {
              setFrontImage(reader.result);
              updateCache("frontImageID", reader.result);
            } else {
              setBackImage(reader.result);
              updateCache("backImageID", reader.result);
            }

            setStep(1);
            resolve();
          };
        },
        error(err) {
          reject(err);
        },
      });
    });
  };

  const handlePlaceholderClick = (e, isFront) => {
    e.preventDefault();
    setDialogOpen(true);
    setIsCapturingFront(isFront);
  };

  const handleLibraryUpload = (e, isFront) => {
    e.preventDefault();

    const inputRef = isFront ? frontImageInputRef : backImageInputRef;
    inputRef.current.click();
  };

  const submitIDCard = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        health_id: getOrderDetails.productId,
        policy_holder_front_id: frontImage,
        policy_holder_back_id: backImage,
      };

      const response = await updateHealthEntry(token, payload);

      if (response.success) {
        navigate(`/get-health-quotes/declaration-one`);
      } else {
        showErrorMessage(`${t("common.error")}: ` + response.message);
      }
    } catch (error) {
      showErrorMessage(t("messages.errorWhileSubmittingIDCard"));
    } finally {
      setLoading(false);
    }
  };

  const requestCameraPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });

      setHasCameraPermission(true);
      stream.getTracks().forEach((track) => track.stop());
      setStep(2);
    } catch (err) {
      const errorMessages = {
        NotAllowedError: t("messages.cameraAccessDenied"),
        NotFoundError: t("messages.noCameraFound"),
        NotSupportedError: t("messages.deviceOrBrowserDoesNotSupport"),
        ConstraintNotSatisfiedError: t("messages.deviceOrBrowserDoesNotSupport"),
      };

      showErrorMessage(errorMessages[err.name] || t("messages.unableToAccessCamera"));
      setHasCameraPermission(false);
    }
  };

  const handleTakePhoto = async (e) => {
    e.preventDefault();
    setDialogOpen(false);

    if (!hasCameraPermission) {
      await requestCameraPermission();
      if (!hasCameraPermission) return;
    }

    setStep(2);
  };

  const handleFileSelect = async (e, isFront) => {
    e.preventDefault();
    setDialogOpen(false);

    const file = e.target.files[0];

    if (!file) {
      showErrorMessage(t("messages.noFileSelected"));
      return;
    }

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedFile(reader.result);
        setIsCapturingFront(isFront);
        requestAnimationFrame(() => setStep(3));
      };
      reader.readAsDataURL(file);
    } else {
      showErrorMessage(t("messages.noFileSelected"));
    }
  };

  const handleCropConfirm = async () => {
    if (!selectedFile) {
      showErrorMessage("No image selected");
      return;
    }

    try {
      const croppedImage = await createCroppedImage(selectedFile);
      if (!croppedImage) throw new Error("Cropping failed");
      await processAndCompressImage(croppedImage);
    } catch (error) {
      console.error("Error processing image:", error);
      showErrorMessage(`${t("messages.failedToProcessImage")}: `+error.message);
    }
  };

  const createCroppedImage = async (fileData) => {
    try {
      const canvas = document.createElement("canvas");
      const image = new Image();
      image.src = fileData;

      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = (err) => {
          console.error("Failed to load image data:", fileData?.slice(0, 100), err);
          reject(new Error("Failed to load image"));
        };
      });

      canvas.width = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;
      const ctx = canvas.getContext("2d");

      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        croppedAreaPixels.width,
        croppedAreaPixels.height
      );

      return canvas.toDataURL("image/jpeg", 0.9);
    } catch (e) {
      console.error("Error creating cropped image:", e);
      showErrorMessage(t("messages.failedToCropImage"));
      return null;
    }
  };

  return (
    <>
      <ImageUploader
        dialogOpen={dialogOpen}
        onDialogClose={() => setDialogOpen(false)}
        onTakePhoto={handleTakePhoto}
        onLibraryUpload={(e) => handleLibraryUpload(e, isCapturingFront)}
      />

      <div
        className="content"
        style={{
          backgroundColor: step !== 1 ? `#071126` : `#fff`,
          maxHeight: step !== 1 ? `calc(100lvh - 0px)` : ``,
          height: step !== 1 ? `100lvh` : ``,
        }}
      >
        <div className="get-quote-container d-m-column">
          <div
            className="get-quote-form-section"
            style={{
              backgroundColor: step !== 1 ? `transparent` : `#fff`,
              marginTop: step !== 1 ? `130px` : ``,
            }}
          >
            <div className="d-flex w-100">
              {step === 1 && (
                <div className="step-content w-100">
                  <h3>{`${t("healthInsurance.idVerification.title")}`}</h3>
                  <ul className="formatted-text">
                    {nricInstructions.map((instruction, index) => (
                      <li key={index}>{instruction}</li>
                    ))}
                  </ul>
            
                  <div className="image-placeholders w-100">
                    <DocumentUploader
                      documentType={
                        <>
                          {`${t("healthInsurance.idVerification.frontNRICCard")}`}
                          <span className="required-asterisk">*</span>
                        </>
                      }
                      image={frontImage}
                      onUploadClick={(e) => handlePlaceholderClick(e, true)}
                      inputRef={frontImageInputRef}
                      onFileSelect={(e) => handleFileSelect(e, true)}
                      showReUpload={!!frontImage}
                    />

                    <DocumentUploader
                      documentType={
                        <>
                          {`${t("healthInsurance.idVerification.backNRICCard")}`}
                          <span className="required-asterisk">*</span>
                        </>
                      }
                      image={backImage}
                      onUploadClick={(e) => handlePlaceholderClick(e, false)}
                      inputRef={backImageInputRef}
                      onFileSelect={(e) => handleFileSelect(e, false)}
                      showReUpload={!!backImage}
                    />
                  </div>
                </div>
              )}

              {step === 2 && (
                <CameraCapture
                  webcamRef={webcamRef}
                  loading={loading}
                  onCapture={capturePhoto}
                  onCancel={() => setStep(1)}
                  title={isCapturingFront ? t("healthInsurance.idVerification.positionMyKadFrontPhoto") : t("healthInsurance.idVerification.positionMyKadBackPhoto") }
                />
              )}

              {step === 3 && (
                <ImageCropper
                  selectedFile={selectedFile}
                  crop={crop}
                  zoom={zoom}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
                  onConfirm={handleCropConfirm}
                  onCancel={() => setStep(1)}
                  title={isCapturingFront ? t("healthInsurance.idVerification.positionMyKadFrontCrop") : t("healthInsurance.idVerification.positionMyKadBackCrop") }
                />
              )}
            </div>
          </div>

          {step === 1 && (
            <>
              <Button
                label={`${t("common.proceed")} (3/7)`}
                className="btn-primary w-100"
                loading={loading}
                disabled={!frontImage || !backImage}
                onClick={(e) => submitIDCard(e)}
                size="large"
              />

              {loading && <Loader />}
            </>
          )}

          <BottomMessagePopUp
            message={errorMessage}
            type="error"
            isVisible={isErrorVisible}
            onClose={() => setIsErrorVisible(false)}
          />
        </div>
      </div>
    </>
  );
};

export default IDVerification;
